import { API_BASE_URL, DEFAULT_HEADERS } from '../../apiConfig';
import { logError } from './llmLogger';

/**
 * ProjectGenerationService
 * Handles AI-powered project generation for course units
 */
export const ProjectGenerationService = {
  /**
   * Generate a complete project with milestones for a course unit
   * @param {Object} params - Project generation parameters
   * @param {string} params.courseId - Course ID
   * @param {string} params.unitId - Unit ID
   * @param {string} params.courseTitle - Course title
   * @param {string} params.courseDescription - Course description
   * @param {string} params.difficultyLevel - Course difficulty level
   * @param {string} params.unitTitle - Unit title
   * @param {string} params.unitObjective - Unit learning objective
   * @param {Array<string>} params.topicsCovered - Topics covered in the unit
   * @param {Object} params.studentProfile - Student profile information
   * @param {string} params.studentProfile.name - Student name
   * @param {string} params.studentProfile.education_level - Education level
   * @param {string} params.studentProfile.career_goals - Career goals
   * @param {string} params.studentProfile.interests - Interests
   * @param {number} params.estimatedHours - Estimated hours for project (default: 20)
   * @param {string} params.projectType - Preferred project type (optional)
   * @returns {Promise<Object>} Generated project data
   */
  async generateProject({
    courseTitle,
    courseDescription,
    difficultyLevel,
    unitTitle,
    unitObjective,
    studentProfile = {},
    estimatedHours = 20,
    projectType = null,
    // Optional fields for custom projects (title/description/metadata)
    projectTitle,
    projectDescription,
    technologies = [],
    learningObjectives = [],
    prerequisites = [],
    deliverables = [],
    maxMilestones = 6,
    maxTasksPerMilestone = 6,
    targetDifficulty,
  }) {
    const payload = {
      user: {
        id: studentProfile?.id ?? 'unknown-user',
        name: studentProfile?.name ?? 'Student',
      },
      project: {
        title: projectTitle || unitTitle || courseTitle || 'Untitled Project',
        description:
          projectDescription || courseDescription || unitObjective || 'No description provided.',
        project_type: projectType ?? null,
        technologies: Array.isArray(technologies) ? technologies : [],
        learning_objectives: Array.isArray(learningObjectives) ? learningObjectives : [],
        prerequisites: Array.isArray(prerequisites) ? prerequisites : [],
        deliverables: Array.isArray(deliverables) ? deliverables : [],
      },
      constraints: {
        maxMilestones,
        maxTasksPerMilestone,
        estimatedHours: estimatedHours ?? 20,
        targetDifficulty: targetDifficulty || difficultyLevel || 'intermediate',
      },
    };

    try {
      const response = await fetch(`${API_BASE_URL}/generate-project`, {
        method: 'POST',
        headers: DEFAULT_HEADERS,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      // Return the generated project with proper structure
      return {
        success: true,
        project: {
          title: data.title,
          description: data.description,
          project_type: data.project_type,
          difficulty_level: data.difficulty_level || difficultyLevel,
          estimated_hours: data.estimated_hours || estimatedHours,
          learning_objectives: data.learning_objectives || [],
          prerequisites: data.prerequisites || [],
          technologies: data.technologies || [],
          deliverables: data.deliverables || [],
          sharing_platforms: data.sharing_platforms || ['github', 'blog'],
          milestones: data.milestones || [],
          ai_generated: true,
          generation_prompt: data.generation_prompt || null,
        },
      };
    } catch (error) {
      logError('ProjectGenerationService', 'generateProject', error, 0);
      return {
        success: false,
        error: error.message || 'Failed to generate project',
      };
    }
  },

  /**
   * Regenerate a project with user feedback
   * @param {Object} params - Regeneration parameters
   * @param {string} params.courseId - Course ID
   * @param {string} params.unitId - Unit ID
   * @param {Object} params.previousProject - Previously generated project
   * @param {string} params.userFeedback - User feedback for regeneration
   * @param {Object} params.studentProfile - Student profile
   * @returns {Promise<Object>} Regenerated project data
   */
  async regenerateProject({ courseId, unitId, previousProject, userFeedback, studentProfile }) {
    const payload = {
      courseId: courseId ?? 'unknown-course',
      unitId: unitId ?? 'unknown-unit',
      previousProject: previousProject ?? {},
      userFeedback: userFeedback ?? 'Please generate a different project.',
      studentProfile: studentProfile ?? {},
    };

    try {
      const response = await fetch(`${API_BASE_URL}/regenerate-project`, {
        method: 'POST',
        headers: DEFAULT_HEADERS,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      return {
        success: true,
        project: {
          title: data.title,
          description: data.description,
          project_type: data.project_type,
          difficulty_level: data.difficulty_level,
          estimated_hours: data.estimated_hours,
          learning_objectives: data.learning_objectives || [],
          prerequisites: data.prerequisites || [],
          technologies: data.technologies || [],
          deliverables: data.deliverables || [],
          sharing_platforms: data.sharing_platforms || ['github', 'blog'],
          milestones: data.milestones || [],
          ai_generated: true,
          generation_prompt: data.generation_prompt || null,
        },
      };
    } catch (error) {
      logError('ProjectGenerationService', 'regenerateProject', error, 0);
      return {
        success: false,
        error: error.message || 'Failed to regenerate project',
      };
    }
  },

  /**
   * Generate additional milestones for an existing project
   * @param {Object} params - Milestone generation parameters
   * @param {string} params.projectId - Project ID
   * @param {string} params.projectTitle - Project title
   * @param {string} params.projectDescription - Project description
   * @param {Array<Object>} params.existingMilestones - Existing milestones
   * @param {number} params.additionalCount - Number of additional milestones to generate
   * @returns {Promise<Object>} Generated milestones
   */
  async generateAdditionalMilestones({
    projectId,
    projectTitle,
    projectDescription,
    existingMilestones = [],
    additionalCount = 1,
  }) {
    const payload = {
      projectId: projectId ?? 'unknown-project',
      projectTitle: projectTitle ?? 'Untitled Project',
      projectDescription: projectDescription ?? 'No description provided.',
      existingMilestones: existingMilestones,
      additionalCount: additionalCount ?? 1,
    };

    try {
      const response = await fetch(`${API_BASE_URL}/generate-milestones`, {
        method: 'POST',
        headers: DEFAULT_HEADERS,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      return {
        success: true,
        milestones: data.milestones || [],
      };
    } catch (error) {
      logError('ProjectGenerationService', 'generateAdditionalMilestones', error, 0);
      return {
        success: false,
        error: error.message || 'Failed to generate milestones',
      };
    }
  },

  /**
   * Generate tasks for a specific milestone
   * @param {Object} params - Task generation parameters
   * @param {string} params.milestoneId - Milestone ID
   * @param {string} params.milestoneTitle - Milestone title
   * @param {string} params.milestoneDescription - Milestone description
   * @param {string} params.projectContext - Project context
   * @param {number} params.taskCount - Number of tasks to generate
   * @returns {Promise<Object>} Generated tasks
   */
  async generateTasks({
    milestoneId,
    milestoneTitle,
    milestoneDescription,
    projectContext,
    taskCount = 5,
  }) {
    const payload = {
      milestoneId: milestoneId ?? 'unknown-milestone',
      milestoneTitle: milestoneTitle ?? 'Untitled Milestone',
      milestoneDescription: milestoneDescription ?? 'No description provided.',
      projectContext: projectContext ?? 'No context provided.',
      taskCount: taskCount ?? 5,
    };

    try {
      const response = await fetch(`${API_BASE_URL}/generate-tasks`, {
        method: 'POST',
        headers: DEFAULT_HEADERS,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      return {
        success: true,
        tasks: data.tasks || [],
      };
    } catch (error) {
      logError('ProjectGenerationService', 'generateTasks', error, 0);
      return {
        success: false,
        error: error.message || 'Failed to generate tasks',
      };
    }
  },

  /**
   * Generate resource recommendations for a project
   * @param {Object} params - Resource generation parameters
   * @param {string} params.projectTitle - Project title
   * @param {Array<string>} params.technologies - Technologies used
   * @param {string} params.difficultyLevel - Difficulty level
   * @param {Array<string>} params.topics - Topics covered
   * @returns {Promise<Object>} Generated resources
   */
  async generateResources({ projectTitle, technologies = [], difficultyLevel, topics = [] }) {
    const payload = {
      projectTitle: projectTitle ?? 'Untitled Project',
      technologies: technologies.length > 0 ? technologies : ['General'],
      difficultyLevel: difficultyLevel ?? 'intermediate',
      topics: topics.length > 0 ? topics : ['General topics'],
    };

    try {
      const response = await fetch(`${API_BASE_URL}/generate-resources`, {
        method: 'POST',
        headers: DEFAULT_HEADERS,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      return {
        success: true,
        resources: data.resources || [],
      };
    } catch (error) {
      logError('ProjectGenerationService', 'generateResources', error, 0);
      return {
        success: false,
        error: error.message || 'Failed to generate resources',
      };
    }
  },

  /**
   * Provide AI feedback on a project submission
   * @param {Object} params - Feedback parameters
   * @param {string} params.projectId - Project ID
   * @param {string} params.projectTitle - Project title
   * @param {string} params.submissionUrl - Submission URL (GitHub, etc.)
   * @param {string} params.submissionDescription - Submission description
   * @param {Array<Object>} params.milestones - Project milestones
   * @param {Array<string>} params.learningObjectives - Learning objectives
   * @returns {Promise<Object>} AI feedback
   */
  async provideFeedback({
    projectId,
    projectTitle,
    submissionUrl,
    submissionDescription,
    milestones = [],
    learningObjectives = [],
  }) {
    const payload = {
      projectId: projectId ?? 'unknown-project',
      projectTitle: projectTitle ?? 'Untitled Project',
      submissionUrl: submissionUrl ?? '',
      submissionDescription: submissionDescription ?? 'No description provided.',
      milestones: milestones,
      learningObjectives: learningObjectives,
    };

    try {
      const response = await fetch(`${API_BASE_URL}/project-feedback`, {
        method: 'POST',
        headers: DEFAULT_HEADERS,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      return {
        success: true,
        feedback: {
          overall_score: data.overall_score || 0,
          strengths: data.strengths || [],
          improvements: data.improvements || [],
          detailed_feedback: data.detailed_feedback || '',
          next_steps: data.next_steps || [],
        },
      };
    } catch (error) {
      logError('ProjectGenerationService', 'provideFeedback', error, 0);
      return {
        success: false,
        error: error.message || 'Failed to generate feedback',
      };
    }
  },
};
